import React, { useEffect } from "react";
import {
  Link,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchUserBookedTicked } from "../redux/reduxThunk/thunkBooking/Booking";
import { checkUserLogout } from "../redux/reduxThunk/thunkAuthUser/AuthUser";
import { toast } from "react-toastify";
import AllUser from "./AllUser";
import CountryVisaList from "./CountryVisaList";
import {
  Home,
  Users,
  Plane,
  Globe,
  Settings,
  BarChart3,
  LogOut,
  Menu,
  X,
  Car,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Search,
  FileSpreadsheet,
  FileText,
  CreditCard,
  Building,
  UserCheck,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import AdminPassengers from "./admin/AdminPassengers";
import AdminCelestialLedger from "./admin/AdminCelestialLedger";
import WalletRequest from "./WalletRequest";
import WalletManagement from "./walletManagement";
import AdminActivity from "./AdminActivities";
import AdminCabPage from "./cab/AdminCabPage";
import AdminVisaApplications from "./admin/AdminVisaApplications";
import AdminCreateVisaApplication from "./admin/AdminCreateVisaApplication";
import AdminHotelPage from "./hotel/AdminHotelPage";
import AdminTourAndTravel from "./AdminTourAndTravel";
import AdminFrontendView from "./admin/AdminFrontendView";
import AdminHotelDoc from "./admin/AdminHotelDoc";
import axios from "axios";
import { Table, Input } from "antd";
import BookingDetailModal from "./admin/BookingDetailModal";
import FlightTicketPage from "./admin/FlightTicketPage";
import {
  getFlightsFromResponse,
  getFareFromResponse,
  getRouteFromSegments,
  groupRoundTripBookings,
} from "../utils/flightBookingUtils";
import AdminCustomFlightModal from "./admin/AdminCustomFlightModal";
const BookingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bookingData = useSelector((state) => state.booking.bookingdata);
  const [loading, setLoading] = useState(true);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [tripTypeFilter, setTripTypeFilter] = useState("All");
  const [viewItem, setViewItem] = useState(null);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await dispatch(fetchUserBookedTicked());
      setLoading(false);
    };
    loadData();
  }, [dispatch]);

  const handleDeleteBooking = async (idOrObj) => {
    let ids = [];
    if (typeof idOrObj === "string") {
      ids = [idOrObj];
    } else if (idOrObj?.type === "roundtrip") {
      ids = [idOrObj.onward?._id, idOrObj.return?._id].filter(Boolean);
    } else if (idOrObj?.booking?._id) {
      ids = [idOrObj.booking._id];
    } else if (idOrObj?._id) {
      ids = [idOrObj._id];
    }

    ids = ids.filter((id) => id && id !== "undefined" && typeof id === "string" && id.trim().length > 0);

    if (ids.length === 0) {
      toast.error("Invalid booking ID");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this flight booking?")) return;
    try {
      const base = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";
      for (const id of ids) {
        await axios.delete(`${base}/api/booking/admin/bookings/${id}`, { withCredentials: true });
      }
      toast.success("Flight booking deleted successfully");
      dispatch(fetchUserBookedTicked());
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete booking");
    }
  };

  const filteredBookings = (bookingData?.data || [])
    ?.filter((booking) => {
      const matchesSearch =
        booking.bookingID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.refID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.mobile?.includes(searchTerm) ||
        booking.passenger?.[0]?.fName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.passenger?.[0]?.lName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.userID?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.userID?.username?.toLowerCase().includes(searchTerm.toLowerCase());

      const bkStatus = (booking.bookingStatus || "").toLowerCase();
      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Confirmed" && (bkStatus === "confirmed" || bkStatus === "success")) ||
        (statusFilter === "Pending" && bkStatus === "pending") ||
        (statusFilter === "Cancelled" && (bkStatus === "cancelled" || bkStatus === "canceled"));

      const isRoundTrip = booking.tripType === "1" || booking.tripType === 1;
      const matchesTripType =
        tripTypeFilter === "All" ||
        (tripTypeFilter === "Round Trip" && isRoundTrip) ||
        (tripTypeFilter === "One Way" && !isRoundTrip);

      return matchesSearch && matchesStatus && matchesTripType;
    })
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  const groupedBookings = groupRoundTripBookings(filteredBookings);
  const filteredGrouped =
    tripTypeFilter === "Round Trip"
      ? groupedBookings.filter((it) => it.type === "roundtrip")
      : tripTypeFilter === "One Way"
        ? groupedBookings.filter((it) => it.type === "single")
        : groupedBookings;
  const sortedGrouped = [...filteredGrouped].sort((a, b) => {
    const getDate = (it) =>
      it.type === "single"
        ? new Date(it.booking?.createdAt || 0).getTime()
        : Math.max(
          new Date(it.onward?.createdAt || 0).getTime(),
          new Date(it.return?.createdAt || 0).getTime()
        );
    return getDate(b) - getDate(a);
  });

  const getStatusCount = (status) => {
    return (
      bookingData?.data?.filter((b) => {
        const s = (b.bookingStatus || "").toLowerCase();
        if (status === "Confirmed") return s === "confirmed" || s === "success";
        if (status === "Pending") return s === "pending";
        if (status === "Cancelled") return s === "cancelled" || s === "canceled";
        return b.bookingStatus === status;
      }).length || 0
    );
  };

  const paginatedBookings = sortedGrouped.slice(0, pageSize);

  // Build export rows from a list of grouped bookings (same shape as table rows, no item ref)
  const buildExportRows = (bookingsList) => {
    const pnrFromRes = (res) => {
      const p = res?.Ticket?.Onward?.passenger || res?.Ticket?.passenger || res?.ticket?.Onward?.passenger || res?.ticket?.passenger || [];
      const arr = Array.isArray(p) ? p : p ? [p] : [];
      return arr[0]?.pnr || null;
    };
    const airlineFromSegs = (segs, fallbackBooking) => {
      if (segs?.[0]?.airName || segs?.[0]?.airlineName || segs?.[0]?.AirlineName || segs?.[0]?.airline) {
        return segs[0].airName || segs[0].airlineName || segs[0].AirlineName || segs[0].airline;
      }
      const res = fallbackBooking?.response;
      if (!res) return "—";
      return (
        res.Flights?.AirlineName ||
        res.Flights?.Onward?.AirlineName ||
        res.flights?.Flights?.AirlineName ||
        res.airlineName ||
        res.AirlineName ||
        "—"
      );
    };
    const dateFromSegs = (segs, fallbackBooking) => {
      const s = segs?.[0];
      if (!s) return fallbackBooking?.createdAt ? new Date(fallbackBooking.createdAt).toLocaleString("en-IN") : "—";
      const d = s.depTime || s.DepartureTime || s.depDateTime || s.depDate;
      if (d) return String(d).length >= 12 ? `${String(d).slice(0, 4)}-${String(d).slice(4, 6)}-${String(d).slice(6, 8)} ${String(d).slice(8, 10)}:${String(d).slice(10, 12)}` : String(d);
      return fallbackBooking?.createdAt ? new Date(fallbackBooking.createdAt).toLocaleString("en-IN") : "—";
    };
    const statusLabel = (s) => (s === "confirmed" || s === "success" ? "Success" : s === "rejected" || s === "cancelled" || s === "canceled" ? "Rejected" : (s || "Success"));
    const rows = [];
    bookingsList.forEach((selectedBooking) => {
      const isRTPaired = selectedBooking.type === "roundtrip";
      const onward = isRTPaired ? selectedBooking.onward : selectedBooking.booking;
      const returnB = isRTPaired ? selectedBooking.return : null;

      const resOn = onward?.response || {};
      const resRet = returnB?.response || {};

      const onwardSegs = getFlightsFromResponse(resOn, "onward");
      const returnSegs = returnB ? getFlightsFromResponse(resRet, "onward") : getFlightsFromResponse(resOn, "return");

      let routeOn = getRouteFromSegments(onwardSegs);
      let routeStr = routeOn ? `${routeOn.dep} - ${routeOn.arr}` : (onward?.refID || "—");

      let routeRet = getRouteFromSegments(returnSegs);
      let routeRetStr = routeRet ? `${routeRet.dep} - ${routeRet.arr}` : (returnB ? returnB?.refID : "");

      const fareOn = getFareFromResponse(resOn);
      const fareRet = returnB ? getFareFromResponse(resRet) : null;
      const fareOnNum = fareOn != null ? (typeof fareOn === "number" ? fareOn : parseFloat(fareOn)) : (onward?.amount || 0);
      const fareRetNum = fareRet != null ? (typeof fareRet === "number" ? fareRet : parseFloat(fareRet)) : (returnB?.amount || 0);
      const totalAmount = fareOnNum + fareRetNum;

      let namesStr = (onward?.passenger || []).map((p) => `${p.title || ""} ${p.fName || ""} ${p.lName || ""}`.trim()).filter(Boolean).join(", ") || "—";
      const statusOn = (onward?.bookingStatus || "success").toLowerCase();
      let statusRet = returnSegs?.length ? ((returnB ? returnB.bookingStatus : onward.bookingStatus) || "success").toLowerCase() : null;

      let pnrOnward = pnrFromRes(resOn) || onward?.refID || "—";
      let airlineOnward = airlineFromSegs(onwardSegs, onward);
      let travelDateOnward = dateFromSegs(onwardSegs, onward);

      let pnrReturn = returnSegs?.length ? (returnB ? pnrFromRes(resRet) || returnB?.refID : pnrFromRes(resOn) || onward?.refID) || "—" : null;
      let airlineReturn = returnSegs?.length ? airlineFromSegs(returnSegs, returnB || onward) : null;
      let travelDateReturn = returnSegs?.length ? dateFromSegs(returnSegs, returnB || onward) : null;

      if (onward?.isCustomFlight && onward?.customFlightDetails) {
        routeStr = `${onward.customFlightDetails.from} - ${onward.customFlightDetails.to}`;
        pnrOnward = onward.customFlightDetails.pnr || pnrOnward;
        let cSegOnward = Array.isArray(onward.customFlightDetails.segments) ? onward.customFlightDetails.segments.find(s => s.direction === "onward") : onward.customFlightDetails.segments?.onward;
        airlineOnward = cSegOnward?.airline || "Custom Flight";
        if (onward.customFlightDetails.departureDate) {
          travelDateOnward = new Date(onward.customFlightDetails.departureDate).toLocaleDateString("en-IN");
          if (onward.customFlightDetails.returnDate) {
            travelDateReturn = new Date(onward.customFlightDetails.returnDate).toLocaleDateString("en-IN");
            routeRetStr = `${onward.customFlightDetails.to} - ${onward.customFlightDetails.from}`;
            pnrReturn = onward.customFlightDetails.returnPnr || pnrOnward;
            let cSegReturn = Array.isArray(onward.customFlightDetails.segments) ? onward.customFlightDetails.segments.find(s => s.direction === "return") : onward.customFlightDetails.segments?.return;
            airlineReturn = cSegReturn?.airline || "Custom Flight";
            statusRet = statusOn;
          }
        }
      }

      const bookedByStr = onward?.userID?.email || onward?.userID?.username || onward?.email || "Unknown";

      const fullRoute = returnSegs?.length || travelDateReturn ? `${routeStr} | ${routeRetStr}` : routeStr;
      const fullAirline = returnSegs?.length || airlineReturn ? `${airlineOnward} | ${airlineReturn}` : airlineOnward;
      const fullPnr = returnSegs?.length || pnrReturn ? `${pnrOnward} | ${pnrReturn}` : pnrOnward;
      const fullTravelDate = returnSegs?.length || travelDateReturn ? `${travelDateOnward} | ${travelDateReturn}` : travelDateOnward;
      const fullStatus = returnSegs?.length || statusRet ? `${statusLabel(statusOn)} | ${statusLabel(statusRet)}` : statusLabel(statusOn);

      rows.push({
        route: fullRoute,
        names: namesStr,
        bookedBy: bookedByStr,
        airline: fullAirline,
        pnr: fullPnr,
        travelDate: fullTravelDate,
        status: fullStatus,
        amount: totalAmount
      });
    });
    return rows;
  };

  const exportFlightBookingsToExcel = () => {
    const exportRows = buildExportRows(sortedGrouped);
    if (exportRows.length === 0) {
      toast.info("No bookings to export.", { position: "top-right", autoClose: 3000 });
      return;
    }
    const headers = ["Route", "Passenger Name", "Booked By", "Airline", "PNR", "Travel Date", "Status", "Amount (₹)"];
    const escapeCsv = (val) => {
      const s = String(val ?? "");
      if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const csvRows = [headers.map(escapeCsv).join(",")];
    exportRows.forEach((r) => {
      csvRows.push([r.route, r.names, r.bookedBy, r.airline, r.pnr, r.travelDate, r.status, r.amount].map(escapeCsv).join(","));
    });
    const csv = "\uFEFF" + csvRows.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `FlightBookings_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export downloaded. Open in Excel.", { position: "top-right", autoClose: 3000 });
  };

  // Build travel info rows for ALL visible bookings (right table) – always visible beside the list
  const travelInfoRows = React.useMemo(() => {
    const pnrFromRes = (res) => {
      const p = res?.Ticket?.Onward?.passenger || res?.Ticket?.passenger || res?.ticket?.Onward?.passenger || res?.ticket?.passenger || [];
      const arr = Array.isArray(p) ? p : p ? [p] : [];
      return arr[0]?.pnr || null;
    };
    const airlineFromSegs = (segs, fallbackBooking) => {
      if (segs?.[0]?.airName || segs?.[0]?.airlineName || segs?.[0]?.AirlineName || segs?.[0]?.airline) {
        return segs[0].airName || segs[0].airlineName || segs[0].AirlineName || segs[0].airline;
      }
      const res = fallbackBooking?.response;
      if (!res) return "—";
      return (
        res.Flights?.AirlineName ||
        res.Flights?.Onward?.AirlineName ||
        res.flights?.Flights?.AirlineName ||
        res.airlineName ||
        res.AirlineName ||
        "—"
      );
    };
    const dateFromSegs = (segs, fallbackBooking) => {
      const s = segs?.[0];
      if (!s) return fallbackBooking?.createdAt ? new Date(fallbackBooking.createdAt).toLocaleString("en-IN") : "—";
      const d = s.depTime || s.DepartureTime || s.depDateTime || s.depDate;
      if (d) return String(d).length >= 12 ? `${String(d).slice(0, 4)}-${String(d).slice(4, 6)}-${String(d).slice(6, 8)} ${String(d).slice(8, 10)}:${String(d).slice(10, 12)}` : String(d);
      return fallbackBooking?.createdAt ? new Date(fallbackBooking.createdAt).toLocaleString("en-IN") : "—";
    };
    const statusLabel = (s) => (s === "confirmed" || s === "success" ? "Success" : s === "rejected" || s === "cancelled" || s === "canceled" ? "Rejected" : (s || "Success"));
    const rows = [];
    paginatedBookings.forEach((selectedBooking) => {
      const isRTPaired = selectedBooking.type === "roundtrip";
      const onward = isRTPaired ? selectedBooking.onward : selectedBooking.booking;
      const returnB = isRTPaired ? selectedBooking.return : null;

      const resOn = onward?.response || {};
      const resRet = returnB?.response || {};

      const onwardSegs = getFlightsFromResponse(resOn, "onward");
      const returnSegs = returnB ? getFlightsFromResponse(resRet, "onward") : getFlightsFromResponse(resOn, "return");

      let routeOn = getRouteFromSegments(onwardSegs);
      let routeStr = routeOn ? `${routeOn.dep} ✈ ${routeOn.arr}` : (onward?.refID || "—");

      let routeRet = getRouteFromSegments(returnSegs);
      let routeRetStr = routeRet ? `${routeRet.dep} ✈ ${routeRet.arr}` : (returnB ? returnB?.refID : "");

      const fareOn = getFareFromResponse(resOn);
      const fareRet = returnB ? getFareFromResponse(resRet) : null;
      const fareOnNum = fareOn != null ? (typeof fareOn === "number" ? fareOn : parseFloat(fareOn)) : (onward?.amount || 0);
      const fareRetNum = fareRet != null ? (typeof fareRet === "number" ? fareRet : parseFloat(fareRet)) : (returnB?.amount || 0);
      const totalAmount = fareOnNum + fareRetNum;

      let namesStr = (onward?.passenger || []).map((p) => `${p.title || ""} ${p.fName || ""} ${p.lName || ""}`.trim()).filter(Boolean).join(", ") || "—";
      const statusOn = (onward?.bookingStatus || "success").toLowerCase();
      let statusRet = returnSegs?.length ? ((returnB ? returnB.bookingStatus : onward.bookingStatus) || "success").toLowerCase() : null;

      let pnrOnward = pnrFromRes(resOn) || onward?.refID || "—";
      let airlineOnward = airlineFromSegs(onwardSegs, onward);
      let travelDateOnward = dateFromSegs(onwardSegs, onward);

      let pnrReturn = returnSegs?.length ? (returnB ? pnrFromRes(resRet) || returnB?.refID : pnrFromRes(resOn) || onward?.refID) || "—" : null;
      let airlineReturn = returnSegs?.length ? airlineFromSegs(returnSegs, returnB || onward) : null;
      let travelDateReturn = returnSegs?.length ? dateFromSegs(returnSegs, returnB || onward) : null;

      if (onward?.isCustomFlight && onward?.customFlightDetails) {
        routeStr = `${onward.customFlightDetails.from} ✈ ${onward.customFlightDetails.to}`;
        pnrOnward = onward.customFlightDetails.pnr || pnrOnward;
        let cSegOnward = Array.isArray(onward.customFlightDetails.segments) ? onward.customFlightDetails.segments.find(s => s.direction === "onward") : onward.customFlightDetails.segments?.onward;
        airlineOnward = cSegOnward?.airline || "Custom Flight";
        if (onward.customFlightDetails.departureDate) {
          travelDateOnward = new Date(onward.customFlightDetails.departureDate).toLocaleDateString("en-IN");
          if (onward.customFlightDetails.returnDate) {
            travelDateReturn = new Date(onward.customFlightDetails.returnDate).toLocaleDateString("en-IN");
            routeRetStr = `${onward.customFlightDetails.to} ✈ ${onward.customFlightDetails.from}`;
            pnrReturn = onward.customFlightDetails.returnPnr || pnrOnward;
            let cSegReturn = Array.isArray(onward.customFlightDetails.segments) ? onward.customFlightDetails.segments.find(s => s.direction === "return") : onward.customFlightDetails.segments?.return;
            airlineReturn = cSegReturn?.airline || "Custom Flight";
            statusRet = statusOn;
          }
        }
      }

      const bookedByStr = onward?.userID?.email || onward?.userID?.username || onward?.email || "Unknown";

      const itemId = isRTPaired ? `rt-${onward._id}-${returnB?._id}` : onward._id;

      rows.push({
        key: itemId,
        item: selectedBooking,
        route: { onward: routeStr, return: returnSegs?.length || travelDateReturn ? routeRetStr : null },
        names: namesStr,
        bookedBy: bookedByStr,
        airline: { onward: airlineOnward, return: airlineReturn },
        pnr: { onward: pnrOnward, return: pnrReturn },
        travelDate: { onward: travelDateOnward, return: travelDateReturn },
        status: { onward: statusLabel(statusOn), return: statusRet ? statusLabel(statusRet) : null },
        statusRaw: { onward: statusOn, return: statusRet },
        amount: totalAmount,
      });
    });
    return rows;
  }, [paginatedBookings]);

  return (
    <div className="flex flex-col h-full">
      {/* Top control bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-gray-600">Show</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-700 bg-white"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
          <select
            value={tripTypeFilter}
            onChange={(e) => setTripTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-700 bg-white hidden sm:block"
          >
            <option value="All">All Trips</option>
            <option value="One Way">One Way</option>
            <option value="Round Trip">Round Trip</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-700 bg-white hidden sm:block"
          >
            <option value="All">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Confirmed">Confirmed</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <button
            type="button"
            onClick={exportFlightBookingsToExcel}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Excel
          </button>
          <button
            type="button"
            onClick={() => setShowCustomModal(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700"
          >
            <Plane className="w-4 h-4" />
            Custom Flight
          </button>
        </div>
        <div className="flex-1 min-w-[200px] max-w-sm lg:max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by ID, name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Single table: Booking + Travel Info side by side – one row per segment, booking card rowSpan */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-white">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-gray-500">Loading bookings...</div>
        ) : travelInfoRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-500">
            <Plane className="w-10 h-10 mb-2 text-gray-300" />
            <p className="text-sm font-medium">No bookings yet</p>
            <p className="text-xs mt-0.5 text-gray-400">Bookings and travel info will appear here</p>
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 border-b-2 border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700 w-52">Booking</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Booked By</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Passenger(s)</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Airline</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">PNR</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Travel Date</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBookings.map((item) => {
                const rows = travelInfoRows.filter((r) => r.item === item);
                if (rows.length === 0) return null;
                const row = rows[0]; // Exactly one row per booking group now
                const d = row.item.type === "roundtrip"
                  ? new Date(Math.min(new Date(row.item.onward.createdAt).getTime(), new Date(row.item.return?.createdAt || row.item.onward.createdAt).getTime()))
                  : new Date(row.item.booking.createdAt);
                const dateStr = d.toLocaleString("en-IN", { dateStyle: "short", timeStyle: "medium" });
                return (
                  <tr key={row.key} className="border-b border-gray-200 hover:bg-gray-50/80">
                    <td className="align-top py-0 pr-0 pl-4 pt-3 pb-3 border-r border-gray-200 bg-[#1e3a5f] text-white w-52">
                      <div className="py-2 pr-3">
                        <div className="font-medium text-white">{row.route.onward}</div>
                        {row.route.return && <div className="font-medium text-white mt-1 text-sm opacity-90">{row.route.return}</div>}
                        <div className="text-xs text-white/80 mt-1">{dateStr}</div>
                        <button
                          type="button"
                          onClick={() => setViewItem(item)}
                          className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      </div>
                    </td>
                    <td className="py-3 px-4 align-top">
                      <div className="flex items-center gap-1.5 text-indigo-700 bg-indigo-50 px-2 py-1 rounded w-max text-xs font-medium border border-indigo-100">
                        <Users className="w-3 h-3" />
                        {row.bookedBy}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-900 align-top">{row.names}</td>
                    <td className="py-3 px-4 text-gray-700 align-top">
                      <div>{row.airline.onward}</div>
                      {row.airline.return && <div className="mt-2 pt-2 border-t border-gray-100">{row.airline.return}</div>}
                    </td>
                    <td className="py-3 px-4 align-top">
                      <span className="text-blue-600 font-medium block">{row.pnr.onward}</span>
                      {row.pnr.return && <span className="text-blue-600 font-medium mt-2 pt-2 border-t border-gray-100 block">{row.pnr.return}</span>}
                    </td>
                    <td className="py-3 px-4 text-gray-700 align-top whitespace-nowrap">
                      <div>{row.travelDate.onward}</div>
                      {row.travelDate.return && <div className="mt-2 pt-2 border-t border-gray-100">{row.travelDate.return}</div>}
                    </td>
                    <td className="py-3 px-4 align-top">
                      <div>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${row.statusRaw.onward === "confirmed" || row.statusRaw.onward === "success" ? "bg-green-100 text-green-800" : row.statusRaw.onward === "rejected" || row.statusRaw.onward === "cancelled" || row.statusRaw.onward === "canceled" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                          {row.status.onward}
                        </span>
                        {!row.item?.isCustomFlight && (
                          <button type="button" onClick={() => navigate("/admin/booking/ticket", { state: { viewItem: row.item, leg: "onward" } })} className="block mt-1 text-xs text-blue-600 hover:underline">
                            Invoice (Onw)
                          </button>
                        )}
                      </div>
                      {row.statusRaw.return && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${row.statusRaw.return === "confirmed" || row.statusRaw.return === "success" ? "bg-green-100 text-green-800" : row.statusRaw.return === "rejected" || row.statusRaw.return === "cancelled" || row.statusRaw.return === "canceled" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                            {row.status.return}
                          </span>
                          {!row.item?.isCustomFlight && (
                            <button type="button" onClick={() => navigate("/admin/booking/ticket", { state: { viewItem: row.item, leg: "return" } })} className="block mt-1 text-xs text-blue-600 hover:underline">
                              Invoice (Ret)
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 align-top">
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => setViewItem(row.item)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 w-fit"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (row.item?.isCustomFlight && row.item?.eTicketPdfPath) {
                              const base = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";
                              window.open(`${base}/${row.item.eTicketPdfPath}`, '_blank');
                            } else {
                              navigate("/admin/booking/ticket", { state: { viewItem: row.item } });
                            }
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 w-fit"
                        >
                          View Ticket
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteBooking(row.item)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded border border-red-200 w-fit mt-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-900 align-top text-right">
                      <span className="font-semibold">₹{Number(row.amount).toLocaleString("en-IN")}</span>
                      <span className="text-xs text-gray-500 block">Total / Net</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {viewItem && (
        <BookingDetailModal
          viewItem={viewItem}
          onClose={() => setViewItem(null)}
        />
      )}

      {showCustomModal && (
        <AdminCustomFlightModal
          onClose={() => setShowCustomModal(false)}
          onSuccess={() => {
            setShowCustomModal(false);
            dispatch(fetchUserBookedTicked());
          }}
        />
      )}
    </div>
  );
};

// Import missing icons
const Clock = ({ size = 20, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const CheckCircle = ({ size = 20, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const Calendar = ({ size = 20, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const SearchSvg = ({ size = 20, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);
///////
const XCircle = ({ size = 20, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="15" y1="9" x2="9" y2="15"></line>
    <line x1="9" y1="9" x2="15" y2="15"></line>
  </svg>
);

// Dashboard Home Component
const DashboardHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bookingData = useSelector((state) => state.booking.bookingdata);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBookings: 0,
    activeVisas: 0,
    revenue: 0,
  });
  const [recentActivities, setRecentActivities] = useState([]);

  const API_BASE_URL = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";

  // Helper function to format time ago
  const getTimeAgo = (dateString) => {
    if (!dateString) return "Unknown";
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hour${Math.floor(diffInSeconds / 3600) > 1 ? 's' : ''} ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} day${Math.floor(diffInSeconds / 86400) > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffInSeconds / 604800)} week${Math.floor(diffInSeconds / 604800) > 1 ? 's' : ''} ago`;
  };

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch bookings
        await dispatch(fetchUserBookedTicked());

        // Fetch users count
        try {
          const usersRes = await axios.get(`${API_BASE_URL}/api/auth/users`, {
            withCredentials: true,
          });
          const usersData = usersRes.data?.data || [];
          setStats((prev) => ({
            ...prev,
            totalUsers: usersRes.data?.totalCount || usersData.length || 0,
          }));
        } catch (error) {
          console.warn("Failed to fetch users:", error.message);
        }

        // Fetch visa count (visa routes are mounted at /api/admin)
        try {
          const visaRes = await axios.get(`${API_BASE_URL}/api/admin/visa-info`, {
            withCredentials: true,
          });
          const visas = visaRes.data?.visas || visaRes.data?.data || [];
          setStats((prev) => ({
            ...prev,
            activeVisas: Array.isArray(visas) ? visas.length : 0,
          }));
        } catch (error) {
          console.warn("Failed to fetch visas:", error.message);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dispatch, API_BASE_URL]);

  // Update stats and activities when booking data changes
  useEffect(() => {
    if (bookingData?.data) {
      const bookings = bookingData.data;

      // Calculate revenue from confirmed bookings
      const confirmedBookings = bookings.filter(
        (b) => b.bookingStatus === "Confirmed"
      );
      const revenue = confirmedBookings.reduce((sum, booking) => {
        // Try multiple possible fields for total amount
        const totalAmount =
          booking?.response?.Fare?.total?.total ||
          booking?.response?.Fare?.PublishedFare ||
          booking?.response?.flights?.Fare?.total?.total ||
          booking?.response?.flights?.Fare?.PublishedFare ||
          booking?.totalAmount ||
          booking?.total ||
          0;
        const amount = typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0;
        return sum + amount;
      }, 0);

      // Update stats
      setStats((prev) => ({
        ...prev,
        totalBookings: bookings.length || 0,
        revenue: revenue || 0,
      }));

      // Generate recent activities from bookings
      const activities = bookings
        .map((booking, index) => {
          const passenger = booking.passenger?.[0];
          const passengerName = passenger
            ? `${passenger.title || ""} ${passenger.fName || ""} ${passenger.lName || ""}`.trim()
            : booking.email || "Unknown User";

          const flightRoute = booking.flightDetails?.onward?.[0]
            ? `${booking.flightDetails.onward[0].depCName || ""} to ${booking.flightDetails.onward[booking.flightDetails.onward.length - 1]?.arrCName || ""}`
            : booking.flightID || "Flight";

          let action = "";
          let type = "booking";

          if (booking.bookingStatus === "Confirmed") {
            action = `Booked flight to ${flightRoute}`;
            type = "booking";
          } else if (booking.bookingStatus === "Cancelled") {
            action = `Cancelled booking ${booking.bookingID || ""}`;
            type = "cancellation";
          } else {
            action = `Pending booking to ${flightRoute}`;
            type = "booking";
          }

          return {
            id: booking._id || index,
            user: passengerName,
            action: action,
            time: getTimeAgo(booking.createdAt),
            type: type,
            createdAt: booking.createdAt,
          };
        })
        .sort((a, b) => {
          // Sort by createdAt descending (most recent first)
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        })
        .slice(0, 5); // Get 5 most recent after sorting

      setRecentActivities(activities);
    }
  }, [bookingData]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Dashboard Overview
        </h1>
        <p className="text-gray-600">
          Welcome back! Here's what's happening with your travel platform.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Users</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                {stats.totalUsers.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Registered users
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Bookings</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                {stats.totalBookings.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                All flight bookings
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Plane className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Visas</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                {stats.activeVisas.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Available visa countries
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Globe className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                ₹{stats.revenue.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                From confirmed bookings
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity & Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">
              Recent Activity
            </h2>
          </div>
          <div className="p-6">
            {recentActivities.length > 0 ? (
              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-2 rounded-lg ${activity.type === "booking"
                          ? "bg-blue-100"
                          : activity.type === "visa"
                            ? "bg-purple-100"
                            : activity.type === "cancellation"
                              ? "bg-red-100"
                              : "bg-gray-100"
                          }`}
                      >
                        {activity.type === "booking" && (
                          <Plane className="w-4 h-4 text-blue-600" />
                        )}
                        {activity.type === "visa" && (
                          <Globe className="w-4 h-4 text-purple-600" />
                        )}
                        {activity.type === "cancellation" && (
                          <XCircle className="w-4 h-4 text-red-600" />
                        )}
                        {activity.type === "profile" && (
                          <Users className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {activity.user}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{activity.action}</p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 ml-2 shrink-0">{activity.time}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Plane className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No recent activities</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">
              Quick Actions
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => navigate("/admin/booking")}
                className="flex flex-col items-center justify-center p-6 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors cursor-pointer active:scale-95"
              >
                <Plane className="w-8 h-8 text-blue-600 mb-2" />
                <span className="text-sm font-medium text-gray-800">
                  New Booking
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  View all bookings
                </span>
              </button>

              <button
                onClick={() => navigate("/admin/visa-management")}
                className="flex flex-col items-center justify-center p-6 bg-purple-50 hover:bg-purple-100 rounded-xl transition-colors cursor-pointer active:scale-95"
              >
                <Globe className="w-8 h-8 text-purple-600 mb-2" />
                <span className="text-sm font-medium text-gray-800">
                  Add Visa
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  Manage visas
                </span>
              </button>

              <button
                onClick={() => navigate("/admin/user")}
                className="flex flex-col items-center justify-center p-6 bg-green-50 hover:bg-green-100 rounded-xl transition-colors cursor-pointer active:scale-95"
              >
                <Users className="w-8 h-8 text-green-600 mb-2" />
                <span className="text-sm font-medium text-gray-800">
                  Add User
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  Manage users
                </span>
              </button>

              <button
                onClick={() => {
                  navigate("/admin/activity");
                  toast.info("View detailed reports and analytics in Activity section", {
                    position: "top-right",
                    autoClose: 3000,
                  });
                }}
                className="flex flex-col items-center justify-center p-6 bg-yellow-50 hover:bg-yellow-100 rounded-xl transition-colors cursor-pointer active:scale-95"
              >
                <BarChart3 className="w-8 h-8 text-yellow-600 mb-2" />
                <span className="text-sm font-medium text-gray-800">
                  Reports
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  View analytics
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Settings Component
const SettingsPage = () => {
  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Settings</h1>
        <p className="text-gray-600">
          Manage your platform settings and preferences
        </p>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <p className="text-gray-500">
          Settings page content will be added here.
        </p>
      </div>
    </div>
  );
};

// Main AdminDashboard Component
const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate(); // Add this
  const dispatch = useDispatch();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("adminSidebarCollapsed") ?? "false");
    } catch {
      return false;
    }
  });

  const toggleSidebarCollapsed = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    try {
      localStorage.setItem("adminSidebarCollapsed", JSON.stringify(next));
    } catch (_) { }
  };

  const [user, setUser] = useState({
    name: "Admin User",
    email: "admin@example.com",
    avatar:
      "https://ui-avatars.com/api/?name=Admin+User&background=0D8ABC&color=fff",
  });

  const navigation = [
    { name: "Dashboard", icon: Home, path: "/admin", exact: true },
    { name: "Bookings", icon: Plane, path: "/admin/booking" },
    { name: "Users", icon: Users, path: "/admin/user" },
    { name: "Passengers", icon: UserCheck, path: "/admin/passengers" },
    { name: "Visa Management", icon: Globe, path: "/admin/visa-management" },
    { name: "Visa Applications", icon: FileText, path: "/admin/visa-applications" },
    { name: "Wallet Request", icon: Globe, path: "/admin/wallet-request" },
    {
      name: "Wallet Management",
      icon: Users,
      path: "/admin/global-wallet-management",
    },
    // {
    //   name: "Celestial Ledger",
    //   icon: CreditCard,
    //   path: "/admin/celestial-ledger",
    // },
    { name: "Activity", icon: BarChart3, path: "/admin/activity" },
    { name: "Cabs", icon: Car, path: "/admin/cabs" },
    { name: "Hotel", icon: Building, path: "/admin/hotel" },
    { name: "Hotel Doc", icon: FileText, path: "/admin/hotel-doc" },
    { name: "Tour and Package", icon: Globe, path: "/admin/tour-and-travel" },
    { name: "Frontend View", icon: Eye, path: "/admin/frontend-view" },
    // { name: "Settings", icon: Settings, path: "/admin/settings" },
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    try {
      // Dispatch logout action
      const result = await dispatch(checkUserLogout()).unwrap();

      // Show success message
      toast.success(result?.message || "Logged out successfully!", {
        position: "top-right",
        autoClose: 3000,
      });

      // Navigate to home page after successful logout
      navigate("/");
    } catch (error) {
      // Handle logout error
      toast.error(error?.message || "Logout failed. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });

      // Even if there's an error, still navigate to home
      navigate("/");
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <div
        className={`
        fixed lg:relative inset-y-0 left-0 z-40 bg-gray-900 text-white transform transition-all duration-300 ease-in-out
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0 lg:flex lg:flex-col
        w-64 ${sidebarCollapsed ? "lg:w-20" : "lg:w-64"}
      `}
      >
        <div className="hidden lg:block absolute -right-3 top-8 z-10">
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            className="p-1.5 rounded-full bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-700 shadow"
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        </div>
        {/* Logo */}
        <div
          className={`border-b border-gray-800 transition-all duration-300 ${sidebarCollapsed ? "p-3 lg:px-3 lg:py-4" : "p-6"
            }`}
        >
          <div
            className={`flex items-center transition-all duration-300 ${sidebarCollapsed ? "justify-center" : "gap-3"
              }`}
          >
            <div className="p-2 bg-blue-600 rounded-lg shrink-0">
              <Plane className="w-6 h-6" />
            </div>
            <div className={`min-w-0 ${sidebarCollapsed ? "lg:hidden" : ""}`}>
              <Link to={"/"} className="text-xl font-bold block truncate">
                Celestial Tours
              </Link>
              <p className="text-xs text-gray-400">Admin Panel</p>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div
          className={`border-b border-gray-800 transition-all duration-300 ${sidebarCollapsed ? "p-3 lg:px-3 lg:py-4" : "p-6"
            }`}
        >
          <div
            className={`flex items-center transition-all duration-300 ${sidebarCollapsed ? "justify-center" : "gap-3"
              }`}
          >
            <img
              src={user.avatar}
              alt={user.name}
              className={`rounded-full border-2 border-blue-500 shrink-0 ${sidebarCollapsed ? "w-10 h-10" : "w-10 h-10"
                }`}
            />
            <div className={`min-w-0 ${sidebarCollapsed ? "lg:hidden" : ""}`}>
              <p className="font-medium truncate">{user.name}</p>
              <p className="text-xs text-gray-400 truncate">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav
          className={`flex-1 overflow-y-auto transition-all duration-300 ${sidebarCollapsed ? "p-2 lg:px-2" : "p-4 space-y-1"
            }`}
        >
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              title={sidebarCollapsed ? item.name : undefined}
              className={`
                flex items-center rounded-lg transition-colors
                ${sidebarCollapsed
                  ? "justify-center p-3 lg:px-3 lg:py-3"
                  : "gap-3 px-4 py-3"
                }
                ${isActive(item.path, item.exact)
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white"
                }
              `}
            >
              <item.icon className="w-5 h-5 shrink-0" />
              <span className={sidebarCollapsed ? "lg:hidden" : ""}>{item.name}</span>
            </Link>
          ))}
        </nav>

        {/* Logout */}
        <div
          className={`border-t border-gray-800 transition-all duration-300 ${sidebarCollapsed ? "p-2 lg:px-2" : "p-4"
            }`}
        >
          <button
            onClick={handleLogout}
            title={sidebarCollapsed ? "Logout" : undefined}
            className={`
              flex items-center w-full rounded-lg transition-colors text-gray-300 hover:bg-gray-800 hover:text-white
              ${sidebarCollapsed ? "justify-center p-3" : "gap-3 px-4 py-3"}
            `}
          >
            <LogOut className="w-5 h-5 shrink-0" />
            <span className={sidebarCollapsed ? "lg:hidden" : ""}>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                {navigation.find((item) => isActive(item.path, item.exact))
                  ?.name || "Dashboard"}
              </h2>
              <p className="text-sm text-gray-600">
                {location.pathname === "/admin" ? "Overview" : "Management"}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="h-6 w-px bg-gray-300"></div>
              <div className="flex items-center gap-3">
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-8 h-8 rounded-full"
                />
                <span className="hidden md:inline text-sm font-medium text-gray-700">
                  {user.name}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main
          className={`flex-1 bg-gray-50 flex flex-col min-h-0 ${location.pathname.startsWith("/admin/activity")
            ? "overflow-hidden"
            : "overflow-y-auto"
            }`}
        >
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="booking" element={<BookingPage />} />
            <Route path="booking/ticket" element={<FlightTicketPage />} />
            <Route path="user" element={<AllUser />} />
            <Route path="passengers" element={<AdminPassengers />} />
            <Route path="visa-management" element={<CountryVisaList />} />
            <Route path="visa-applications" element={<AdminVisaApplications />} />
            <Route path="visa-applications/create" element={<AdminCreateVisaApplication />} />
            <Route path="wallet-request" element={<WalletRequest />} />
            <Route
              path="global-wallet-management"
              element={<WalletManagement />}
            />
            <Route path="celestial-ledger" element={<AdminCelestialLedger />} />
            <Route path="activity" element={<AdminActivity />} />
            <Route path="cabs/*" element={<AdminCabPage />} />
            <Route path="hotel/*" element={<AdminHotelPage />} />
            <Route path="hotel-doc" element={<AdminHotelDoc />} />
            <Route path="tour-and-travel" element={<AdminTourAndTravel />} />
            <Route path="frontend-view" element={<AdminFrontendView />} />
            {/* <Route path="settings" element={<SettingsPage />} /> */}
          </Routes>
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
